import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { TYPE_OPTIONS, PAY_COLS, inr, invoicesCsv, downloadCsv } from "@/lib/serviceInvoiceReport";
import ServiceInvoiceDayTable from "@/components/serviceInvoiceReport/ServiceInvoiceDayTable";
import BreakdownTable from "@/components/serviceInvoiceReport/BreakdownTable";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${today().slice(0, 8)}01`;
const FIELD = "border border-canvas-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink";

function Kpis({ t }) {
  const items = [["Invoices", t.count, true], ["Total value", t.total_invoice_value], ["Labour", t.labour], ["Spares", t.spares],
    ["Warranty parts", t.warranty_part_value], ...PAY_COLS.map(([k, l]) => [l, t[k]])];
  return (
    <div className="grid grid-cols-3 lg:grid-cols-9 gap-2" data-testid="sir-kpis">
      {items.map(([l, v, plain]) => (
        <div key={l} className="border border-canvas-line bg-white px-3 py-2">
          <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-ink-muted">{l}</p>
          <p className="font-bold text-base">{plain ? v : inr(v)}</p>
        </div>
      ))}
    </div>
  );
}

export default function ServiceInvoiceReportPage() {
  const [f, setF] = useState({ date_from: monthStart(), date_to: today(), branch_id: "", invoice_type: "", search: "" });
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data: d } = await api.get("/reports/service-invoices", { params });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, []);

  return (
    <div data-testid="service-invoice-report-page">
      <PageHeader eyebrow="Service / Reports" title="Service Invoice Report" subtitle="Day-wise totals per branch — click a day to see its invoices." />
      <div className="px-8 py-6 space-y-5">
        <div className="flex items-end gap-2 flex-wrap">
          <input type="date" value={f.date_from} onChange={set("date_from")} className={FIELD} data-testid="sir-from" />
          <input type="date" value={f.date_to} onChange={set("date_to")} className={FIELD} data-testid="sir-to" />
          <select value={f.branch_id} onChange={set("branch_id")} className={FIELD} data-testid="sir-branch">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={f.invoice_type} onChange={set("invoice_type")} className={FIELD} data-testid="sir-type">
            {TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input value={f.search} onChange={set("search")} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Customer, vehicle or invoice no." className={`${FIELD} pl-8 w-64`} data-testid="sir-search" />
          </div>
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-white text-sm font-semibold disabled:opacity-50" data-testid="sir-apply">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Apply
          </button>
          <button
            onClick={() => downloadCsv(`service-invoices_${f.date_from}_${f.date_to}.csv`, invoicesCsv(data?.days || []))}
            disabled={!data?.days?.length}
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-ink text-sm font-semibold disabled:opacity-40"
            data-testid="sir-export"
          >
            <DownloadSimple size={14} /> Export CSV
          </button>
        </div>
        {data && (
          <>
            <Kpis t={data.totals} />
            <ServiceInvoiceDayTable days={data.days} totals={data.totals} />
            <div className="grid lg:grid-cols-3 gap-4">
              <BreakdownTable title="By invoice type" rows={data.by_type} testId="sir-by-type" />
              <BreakdownTable title="By technician" rows={data.by_technician} testId="sir-by-tech" />
              <BreakdownTable title="By advisor" rows={data.by_advisor} testId="sir-by-advisor" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
